import React, { useContext, useState, useCallback, useRef, useEffect } from 'react';
import { ProductDetail } from '../types/ProductTypes';

type ProductEditContextType = {
    hasUnsavedChanges: boolean;
    setHasUnsavedChanges: (value: boolean) => void;
    pendingChanges: Partial<ProductDetail>;
    setPendingChanges: (changes: Partial<ProductDetail>) => void;
    mergePendingChanges: (changes: Partial<ProductDetail>) => void;
    clearPendingChanges: () => void;
    getPendingChanges: () => Partial<ProductDetail>;
    registerSaveHandler: (handler: () => Promise<void>) => void;
    registerCancelHandler: (handler: () => void) => void;
    saveAllChanges: () => Promise<void>;
    cancelAllChanges: () => void;
};

export const ProductEditContext = React.createContext<ProductEditContextType>(undefined!);

interface ProductEditContextProviderProps {
    children: React.ReactNode;
}

export const ProductEditContextProvider = ({ children }: ProductEditContextProviderProps) => {
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
    const [pendingChanges, setPendingChanges] = useState<Partial<ProductDetail>>({});
    const [saveHandlers, setSaveHandlers] = useState<Array<() => Promise<void>>>([]);
    const [cancelHandlers, setCancelHandlers] = useState<Array<() => void>>([]);

    // Ref to always have the latest pendingChanges available synchronously
    const pendingChangesRef = useRef<Partial<ProductDetail>>({});

    // Keep ref in sync with state - this updates synchronously within the setState callback
    const mergePendingChanges = useCallback((changes: Partial<ProductDetail>) => {
        setPendingChanges((prev) => {
            const newState = { ...prev, ...changes };
            // Update ref synchronously within setState to ensure it's always current
            pendingChangesRef.current = newState;
            return newState;
        });
        setHasUnsavedChanges(true);
    }, []);

    const clearPendingChanges = useCallback(() => {
        pendingChangesRef.current = {};
        setPendingChanges({});
        setHasUnsavedChanges(false);
    }, []);

    // Function to get the current pending changes (reads from ref for synchronous access)
    const getPendingChanges = useCallback(() => {
        return pendingChangesRef.current;
    }, []);

    const registerSaveHandler = useCallback((handler: () => Promise<void>) => {
        setSaveHandlers((prev) => [...prev, handler]);
        return () => {
            setSaveHandlers((prev) => prev.filter((h) => h !== handler));
        };
    }, []);

    const registerCancelHandler = useCallback((handler: () => void) => {
        setCancelHandlers((prev) => [...prev, handler]);
        return () => {
            setCancelHandlers((prev) => prev.filter((h) => h !== handler));
        };
    }, []);

    const saveAllChanges = useCallback(async () => {
        // Call all registered save handlers
        for (const handler of saveHandlers) {
            await handler();
        }
        clearPendingChanges();
    }, [saveHandlers, clearPendingChanges]);

    const cancelAllChanges = useCallback(() => {
        // Call all registered cancel handlers
        for (const handler of cancelHandlers) {
            handler();
        }
        clearPendingChanges();
    }, [cancelHandlers, clearPendingChanges]);

    return (
        <ProductEditContext.Provider
            value={{
                hasUnsavedChanges,
                setHasUnsavedChanges,
                pendingChanges,
                setPendingChanges,
                mergePendingChanges,
                clearPendingChanges,
                getPendingChanges,
                registerSaveHandler,
                registerCancelHandler,
                saveAllChanges,
                cancelAllChanges,
            }}
        >
            {children}
        </ProductEditContext.Provider>
    );
};

export const useProductEdit = () => useContext(ProductEditContext);
